/*
 * CUSTOMER_USER: Can only manage licenses from the specific customer
 * CUSTOMER_ADMIN: CUSTOMER_USER + can manage CUSTOMER_USER for the associated customer
 * FEIG_ADMIN: CUSTOMER_USER + CUSTOMER_ADMIN + can manage CUSTOMER_ADMINs of all customers
 * */

import { Alpha2Code, getAlpha2Codes } from 'i18n-iso-countries';

export enum EAppPermissions {
  View_GateControlName = 'View_GateControlName',
  View_Serialnumber = 'View_Serialnumber',
  View_SWVersion = 'View_SWVersion',
  View_SWVersionIOProc = 'View_SWVersionIOProc',
  View_Doorcycles = 'View_Doorcycles',
  View_Componentslist = 'View_Componentslist',
  Exec_FactorySetting = 'Exec_FactorySetting',
  Exec_ControllerReset = 'Exec_ControllerReset',
  View_Customer_Serialnumber = 'View_Customer_Serialnumber',
  View_Customer_Doortype = 'View_Customer_Doortype',
  View_Customer_YOC = 'View_Customer_YOC',
  View_Customer_Doorsize = 'View_Customer_Doorsize',
  Do_PasswordP999 = 'Do_PasswordP999',
  Read_Parameters = 'Read_Parameters',
  Edit_Parameters = 'Edit_Parameters',
  Write_Parameters = 'Write_Parameters',
  Export_Parameters = 'Export_Parameters',
  Import_Parameters = 'Import_Parameters',
  Switch_Display = 'Switch_Display',
  Use_Keypad = 'Use_Keypad',
  View_Inputs = 'View_Inputs',
  Edit_Inputs = 'Edit_Inputs',
  View_Outputs = 'View_Outputs',
  View_Faults = 'View_Faults',
  Export_Faults = 'Export_Faults',
  Read_Timeswitch = 'Read_Timeswitch',
  Edit_Timeswitch = 'Edit_Timeswitch',
  Write_Timeswitch = 'Write_Timeswitch',
  Import_Timeswitch = 'Import_Timeswitch',
  Export_Timeswitch = 'Export_Timeswitch',
  Write_TimeswitchClock = 'Write_TimeswitchClock',
  Read_Log = 'Read_Log',
  Convert_Log = 'Convert_Log',
  Export_Log = 'Export_Log',
  Use_PasswordCalculator = 'Use_PasswordCalculator',
  Use_Simulator = "Use_Simulator",
  Use_DoR_Recordguide = "Use_DoR_Recordguide"
}

export enum EManagementRoles {
  CUSTOMER_USER = 'CUSTOMER_USER',
  CUSTOMER_ADMIN = 'CUSTOMER_ADMIN',
  FEIG_ADMIN = 'FEIG_ADMIN',
}

export enum EOfflineRevalidationTime {
  '1D' = '1d',
  '7D' = '7d',
  '14D' = '14d',
  '30D' = '30d',
}

export enum EMaxValidityPeriod {
  '1M' = '1M',
  '3M' = '3M',
  '6M' = '6M',
  '12M' = '12M',
}

export enum EAppLang {
  DE = 'de',
  EN = 'en',
}

export enum EAppRoles {
  'BASIC' = 'BASIC',
  'ENDUSER' = 'ENDUSER',
  'MECHANIC' = 'MECHANIC',
  'DISTRIBUTOR' = 'DISTRIBUTOR',
  'SUPPORT' = 'SUPPORT',
  'DoR_INSTALLER' = "DoR_INSTALLER"
}

export enum EAppRoleIds {
  'BASIC' = 1,
  'ENDUSER' = 2,
  'DISTRIBUTOR' = 3,
  'MECHANIC' = 4,
  'SUPPORT' = 5,
  'DoR_INSTALLER' = 6
}


export type IAppUserCustomer_1_2_0 = {
  id: number,
  HC: string;
  KU: string;
  KUNR: string;
  ROLE: EAppRoles | string;
  ROLE_ID: number;
  GRANTS: EAppPermissions[];
  REVOKES: EAppPermissions[];
  OFFLINE_REVALIDATION_TIME: EOfflineRevalidationTime;
  EXPIRES_AT?: number;
  COMPANY: string;
  PERMISSIONDOMAINS: IAppPermissionDomain[];
  CUSTOMER_NUMBERS: string[];
  PERMISSION_NUMBERS: string[];
}

export type IAppUserCustomer = {
  AUTH_KEY: string[];
  SUPPORT_EMAIL: string;
} & Omit<IAppUserCustomer_1_2_0, 'ROLE_ID'>;

export type CombinedAppUserCustomers = IAppUserCustomer | IAppUserCustomer_1_2_0;

// the original interface
export interface IAppUserJwtContent {
  readonly aud: string;
  iss: string;
  readonly alg: string;

  // jwt specific variable
  iat: number; // issued at timestamp
  jti: string; // unique id
  cat: number; // changed at
  name: string; //hash name
  nbf?: number;
  exp: number;
  licenseExp: number;
  refreshExp: number;
  licenseVersion?: string;

  // user specific
  company: string;
  email: string;
  sub: string; // userid: mail@mail.de
  firstname: string;
  lastname: string;

  // license specific

  customers: IAppUserCustomer[];
  customerCode: string[];
  role?: EAppRoles | string;
  grants: EAppPermissions[];
  revokes: EAppPermissions[];
  offlineRevalidationTime: EOfflineRevalidationTime;
}


// License v 1.2.0
export type IAppUserJwtContent_1_2_0 = Omit<
  IAppUserJwtContent,
  'company' | 'customerCode' | 'licenseExp' | 'role' | 'grants' | 'revokes' | 'offlineRevalidationTime' | 'customers'
> & {
  customers: IAppUserCustomer_1_2_0[];
};

export interface IManagementUser {
  id?: number;
  email?: string;
  firstname?: string;
  lastname?: string;
  passwordhash?: string;
  password?: string;
  lastLogin?: Date;
  createdAt?: Date;
  updatedAt?: Date;
  role?: EManagementRoles;
  customer?: number | ICustomer;
  customerId?: number;
}

export type SsoAppRoleMapping = {
  entraRole: string;
  appRole: string;
};


export type SsoGroupMapping =
  | SsoAppRoleMapping;


export interface ICustomer {
  id?: number;
  appUserCustomerAccess?: number | IAppUserCustomerAccess[];
  name: string;
  authKey: string[];
  host: string;
  hc: string;
  ku: string;
  kuNr: string;
  supportEmail?: string;
  customerCode?: string;
  licenseEmail?: string;
  dataEmail?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: Alpha2Code;
  web?: string;
  phone?: string;
  appsplashlogo?: Blob;
  appcolorscheme?: IAppColorScheme;
  licensemanagerconfig?: ILicensemanagerConfig;
  passwordconf?: ICalcConfig[];
  createdAt?: Date;
  updatedAt?: Date;
  feigCustomerNumbers?: string[];
  ssoEntraTenantId?: string;
  ssoGroupMappings?: SsoGroupMapping[] | null;
}

/**
 * FEIG 2000 Customers
 */
export interface IFeigCustomer {
  id: number;
  name: string;
  createdAt: Date;
  updatedAt: Date;
}

export class IAppUser {
  id?: number;
  username?: string;
  email?: string;
  firstname?: string;
  lastname?: string;
  password?: string;
  lastLogin?: Date;
  appVersion?: string;
  registertans?: IRegisterTan[] | string[] | Promise<IRegisterTan[]>;
  confirmRegisterUntil?: Date;
  hash?: string;
  legacyHash?: string;
  jwt?: string;
  customeraccess?: Partial<IAppUserCustomerAccess>[];
  createdAt?: Date;
  updatedAt?: Date;
  userIsConfirmed?: boolean;
  entraId?: string;
  entraTenantId?: string;
  ssoType?: string;
}

export type SSOType = undefined | 'personal' | 'feig' | 'customer';

export type IAppUserCustomerAccess = {
  id?: number;
  user?: IAppUser;
  userId?: number;
  company: string;
  offlineRevalidationTime: EOfflineRevalidationTime;
  expiresAt?: Date;
  appRole?: IAppRole;
  appRoleId?: number;
  role?: EAppRoles;
  customer?: number | ICustomer;
  customerId?: number;
  grants?: EAppPermissions[];
  revokes?: EAppPermissions[];
  appCustomerPermissions: IAppPermission[];
  feigCustomerNumbers?: string[];
  permissionNumbers?: string[];
  createdAt?: Date;
  updatedAt?: Date;
};


// Alte Struktur
export interface IInternationalText {
  id?: number;
  texts: ITranslation[]
}

// Neue Struktur
export interface ITranslationMap {
  [key: string]: string;  // z.B. "DE": "Grundfunktion", "EN": "Basic"
}

// Aktualisierte Interfaces für Rollen mit der flexiblen Textstruktur
export type IAppRight = {
  id?: number;
  uniqueName: string;
  name?: IInternationalText;
  description?: IInternationalText;
  sort?: number
}

export type IAppRole = {
  id?: number;
  name: IInternationalText;
  description?: IInternationalText;
  roleRights?: IAppRoleRight[];
  roletype?: 'globalRole' | 'customerRole';
}

type IAppRoleAllowDeny = {
  roleRights: IAppRoleRight[];
}

export type IAppGlobalRole = {
  uniqueName: string;
} & IAppRole;

export type IAppCustomerRole = {
  inherits?: IAppRole;
  inheritsId?: number;
  stdRoleEquivalent?: number;
  uniqueName?: string;
  customer?: ICustomer;
  customerId?: number;
} & IAppRole;

export type IAppRoleWithAllowDeny = IAppRole & IAppRoleAllowDeny
export type IAppCustomerRoleWithAllowDeny = IAppCustomerRole & IAppRoleAllowDeny & { inherits?: IAppGlobalRoleWithAllowDeny };
export type IAppGlobalRoleWithAllowDeny = IAppGlobalRole & IAppRoleAllowDeny

export type IAppRoleRight = {
  id?: number;
  role?: IAppRole;
  roleId?: number;
  right?: IAppRight;
  rightId?: number;
  state: 'allow' | 'deny';
  inherited?: boolean;
}

export enum Languages {
  EN = "EN",
  DE = "DE",
  FR = "FR",
  IT = "IT",
  ES = "ES",
  NL = 'NL'
}


export type ITranslation = {
  id?: number;
  text: string;
  language: LanguageValues
  internationalText?: IInternationalText;
  internationalTextId?: number;
}

export type LanguageValues = `${Languages}`; // this equals 'foo' | 'bar'


export type IAppUserCustomerDomainPermission = {
  id?: number;
  domain: IAppPermissionDomain;
  allowedPermission: IAppPermission[];
};

export interface ILicensemanagerConfig {
  initialUserRole: { auto: number | undefined; manual: number; sso?: number | undefined };
  defaultGrants: EAppPermissions[];
  defaultRevokes: EAppPermissions[];
  defaultOfflineRevalidationTime: EOfflineRevalidationTime;
  defaultMaxValidityPeriod: EMaxValidityPeriod;
}

export const defaultLicensemanagerConfig = {
  initialUserRole: {
    auto: null,
    manual: 1,
    sso: null,
  },
  defaultGrants: [],
  defaultRevokes: [],
  defaultOfflineRevalidationTime: EOfflineRevalidationTime['30D'],
  defaultMaxValidityPeriod: null,
};

/*
 * https://ionicframework.com/docs/theming/color-generator
 * */

export interface IColorParts {
  main?: string;
  rgb?: string;
  contrast?: string;
  contrastrgb?: string;
  shade?: string;
  tint?: string;
}

export const defaultColorParts = {
  main: '',
  rgb: '',
  contrast: '',
  contrastrgb: '',
  shade: '',
  tint: '',
};

export interface IAppColorScheme {
  splashImageBgColor?: string;
  textColor?: string;
  stylesheet?: string; // optional, for custom styles
  primary?: IColorParts;
  secondary?: IColorParts;
  tertiary?: IColorParts;
  success?: IColorParts;
  warning?: IColorParts;
  danger?: IColorParts;
  dark?: IColorParts;
  medium?: IColorParts;
  light?: IColorParts;
  white?: IColorParts;
}

export const defaultAppColorScheme = {
  splashImageBgColor: '',
  textColor: '',
  stylesheet: '',
  primary: { ...defaultColorParts },
  secondary: { ...defaultColorParts },
  tertiary: { ...defaultColorParts },
  success: { ...defaultColorParts },
  warning: { ...defaultColorParts },
  danger: { ...defaultColorParts },
  dark: { ...defaultColorParts },
  medium: { ...defaultColorParts },
  light: { ...defaultColorParts },
  white: { ...defaultColorParts },
};

export interface ICalcLevel {
  name: { [key: string]: string }; // de en
  staticPassword?: number;
  generator?: number;
  offset?: number;
  disallowedNumbers?: Array<number>;
  replaceNumber?: number;
  disallow0Input?: boolean;
}

export enum ECalulators {
  STD = 'STD',
  RY = 'RY',
}

export interface ICalcConfig {
  calculator?: ECalulators;
  version?: string;
  levels: ICalcLevel[];
  virtualDipPw?: string;
}

export const defaultCalcConfig: ICalcConfig[] = [
  {
    calculator: ECalulators.STD,
    levels: [],
    version: '1.0',
  },
];

export enum LegalTextType {
  DATA_PROTECTION = 'DataProtection',
  TERMS_OF_USE = 'TermsOfUse',
  IMPRINT = 'Imprint',
}

export enum LegalTextLang {
  DE = 'DE',
  EN = 'EN',
}

export interface ILegalText {
  id?: number;
  type: LegalTextType;
  lang: LegalTextLang;
  date?: string;
  text: string;
  version?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IRegisterTanClaim  {
  id?: number;
  claimedAt: Date;
  userId: number;
  user?: IAppUser;
  registerTan?: IRegisterTan;
  registerTanId?: number;
};



export class IRegisterTan {
  id?: number;
  tan?: string;
  role?: EAppRoles; // ENUMs
  appRole?: IAppRole
  appRoleId?: number
  customer?: number | ICustomer;
  customerId?: number;
  claims?: IRegisterTanClaim[];
  maxUsageCount?: number;
  createdAt?: Date;
  appPermissions?: IAppPermission[];
}

export class CreateRegisterTanDto extends IRegisterTan {
  numberToGenerate = 1;
}

export type EnvironmentName = 'test' | 'development' | 'staging' | 'production';

export type EnvConfig = {
  environment: {
    runtime: EnvironmentName;
    deployment: EnvironmentName;
  };
  version: string;
};

export interface IAppPermission {
  id?: number;
  permissionNumber?: number;
  domainId?: number;
  domain?: IAppPermissionDomain;
  name?: string;
  children: IAppPermission[];
}

export interface IAppPermissionSelect extends IAppPermission {
  selected: boolean;
}

export interface IAppPermissionDomain {
  id?: number;
  feigCustomerNumbers?: string[];
  customer?: ICustomer;
  customerId?: number;
  appPermissions?: IAppPermission[];
}
